import { registerLocaleData } from '@angular/common';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import localeDe from '@angular/common/locales/de';
import localeEs from '@angular/common/locales/es';
import localeFr from '@angular/common/locales/fr';
import {
  ApplicationConfig,
  inject,
  LOCALE_ID,
  provideAppInitializer,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { provideTranslateLoader, provideTranslateService } from '@ngx-translate/core';
import { APP_ROUTES } from './app.routes';
import { AuthService } from './core/auth/auth.service';
import { DEFAULT_LANG } from './core/i18n/i18n.const';
import { LanguageService } from './core/i18n/language.service';
import { KcTranslateLoader } from './core/i18n/translate-loader';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { errorInterceptor } from './core/interceptors/error.interceptor';
import { tokenRefreshInterceptor } from './core/interceptors/token-refresh.interceptor';

// Données de locale des quatre langues servies. Sans cet enregistrement, Angular n'embarque que
// `en-US` et un format de date symbolique sortirait en anglais quelle que soit la langue active.
// L'anglais n'a pas à être enregistré : c'est la locale compilée par défaut.
registerLocaleData(localeFr);
registerLocaleData(localeDe);
registerLocaleData(localeEs);

/**
 * Configuration racine de l'application.
 *
 * @remarks
 * Application ZONELESS : `provideZonelessChangeDetection()` et aucun import de zone.js
 * (ni dépendance npm, ni polyfill). Tout état est porté par des signals — ne jamais
 * souscrire manuellement à un Observable sans repasser par un signal.
 */
export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),

    provideRouter(APP_ROUTES, withComponentInputBinding()),

    // Ordre significatif : le refresh préemptif ne touche pas la réponse ; authInterceptor
    // rattrape le 401 réactif (errorInterceptor, en aval, laisse passer les 401 exprès)
    provideHttpClient(
      withFetch(),
      withInterceptors([tokenRefreshInterceptor, authInterceptor, errorInterceptor]),
    ),

    // Traductions : repli par CLÉ sur le français, jamais sur la clé brute
    provideTranslateService({
      fallbackLang: DEFAULT_LANG,
      loader: provideTranslateLoader(KcTranslateLoader),
    }),

    // La langue doit être chargée AVANT le premier rendu, sinon l'écran s'affiche en français
    // puis clignote vers la langue cible. La session, elle, se restaure en tâche de fond : la
    // fiche n'a pas à attendre le profil, qui ne fait qu'affiner l'affichage (nom, langue).
    provideAppInitializer(async () => {
      // Les deux inject() AVANT le premier await : hors contexte d'injection, ils lèveraient NG0203
      const language = inject(LanguageService);
      const auth = inject(AuthService);

      await language.initialize();
      auth.restoreSession();
    }),

    // Locale de formatage figée au démarrage : `LOCALE_ID` est lu une fois par les pipes, il ne
    // suit donc pas une bascule à chaud. Les dates qui doivent la suivre passeront par un pipe
    // dédié lisant la langue active (étape « dates » du plan).
    {
      provide: LOCALE_ID,
      useFactory: () => inject(LanguageService).locale(),
    },
  ],
};
